import type { Analytics } from 'firebase/analytics';
import { getConsent, onConsentChange } from './consent';
import { firebaseConfig } from './firebase-config';

/**
 * Аналітика.
 *
 * Набір подій паритетний із мобільними клієнтами (`analytics_service.dart`),
 * за винятком тих, яких у вебі не існує: `switch_camera` і `remote_control` —
 * браузер цих дій не виконує.
 *
 * Crashlytics не переноситься: для вебу його не існує.
 *
 * ⚠️ GDPR (зміна onboarding-share-first): без згоди Firebase не завантажується
 * взагалі — ні бібліотеки, ні cookie `_ga*`, ні запитів. Події до згоди не
 * накопичуються. Cookie обмежені хостом one2one, щоб відкликання не чіпало
 * статистику основного сайту на `.hollybyte.com`.
 *
 * Firebase вантажиться динамічно й лише за наявності повної конфігурації:
 * аналітика не має ані блокувати старт дзвінка, ані валити застосунок, якщо
 * її заблокував блокувальник реклами.
 */
export type AnalyticsEvent =
  | 'call_started'
  | 'call_ended'
  | 'room_established'
  | 'connection_error'
  | 'reconnection_attempt'
  | 'reconnection_failed'
  | 'ice_selected'
  | 'media_toggle'
  | 'screen_share_started'
  | 'screen_share_stopped'
  | 'copy_room_link'
  | 'copy_room_id'
  | 'share_room'
  | 'schedule_call'
  | 'room_created'
  | 'room_joined'
  | 'guest_screen_shown'
  | 'invite_card_shown';

type Params = Record<string, string | number | boolean>;

let analytics: Analytics | null = null;
let initializing: Promise<void> | null = null;

async function ensureAnalytics(): Promise<void> {
  if (analytics !== null) return;
  const config = firebaseConfig();
  if (config === null) return;

  const [{ initializeApp }, { initializeAnalytics, isSupported }] = await Promise.all([
    import('firebase/app'),
    import('firebase/analytics'),
  ]);

  if (!(await isSupported())) return;
  analytics = initializeAnalytics(initializeApp(config), {
    config: { cookie_domain: window.location.hostname },
  });
}

/** Завантажує аналітику, лише якщо користувач дозволив. */
export function initAnalytics(): void {
  if (getConsent() !== 'granted') return;
  initializing ??= ensureAnalytics().catch(() => {
    // Блокувальник, приватний режим, офлайн — усе це не є підставою
    // зривати роботу застосунку.
  });
}

/** Cookie статистики, які поставив сайт: `_ga` і `_ga_<id>`. */
function deleteAnalyticsCookies(): void {
  const host = window.location.hostname;
  for (const part of document.cookie.split(';')) {
    const name = part.split('=')[0]?.trim() ?? '';
    if (name !== '_ga' && !name.startsWith('_ga_')) continue;
    document.cookie = `${name}=; Max-Age=0; path=/`;
    document.cookie = `${name}=; Max-Age=0; path=/; domain=${host}`;
  }
}

async function setCollection(enabled: boolean): Promise<void> {
  if (analytics === null) return;
  const { setAnalyticsCollectionEnabled } = await import('firebase/analytics');
  setAnalyticsCollectionEnabled(analytics, enabled);
}

// Згода змінилась: «Дозволити» вантажить і вмикає, відкликання вимикає
// й прибирає cookie.
onConsentChange((consent) => {
  if (consent === 'granted') {
    initAnalytics();
    void setCollection(true).catch(() => undefined);
  } else {
    void setCollection(false).catch(() => undefined);
    deleteAnalyticsCookies();
  }
});

export function logEvent(name: AnalyticsEvent, params: Params = {}): void {
  if (getConsent() !== 'granted') return;
  void (async (): Promise<void> => {
    try {
      initAnalytics();
      await initializing;
      if (analytics === null || getConsent() !== 'granted') return;
      const { logEvent: log } = await import('firebase/analytics');
      log(analytics, name, { platform: 'web', ...params });
    } catch {
      // Аналітика ніколи не ламає дзвінок.
    }
  })();
}
